/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	CosmosComponentContextProps,
	DividerSection,
	getHero,
	heroOwnsHeading,
	getSections,
	useCosmosComponentContext,
} from "@cosmos/components";
import { CosmosEnumDirectoryVariant, CosmosEnumSectionSizes } from "@cosmos/hygraph-schema";
import { useAuth } from "@cosmos/next-hooks";

import { TemplateProviders } from "../templates/TemplateProviders";

import { DirectoryCmsBody } from "./DirectoryCmsBody";
import { InstructorSpotlight } from "./InstructorSpotlight";

import type { DirectoryCmsBodyData, DirectoryBaseChrome, DirectoryServiceChrome } from "./DirectoryCmsBody";
import type { DirectoryPageData } from "./types";

interface Props {
	pageData: DirectoryPageData;
	componentContext?: CosmosComponentContextProps;
}

/**
 * Page-data → `DirectoryCmsBody` data shape. Cast-free translation:
 * the discriminated `switch` lets TS verify each variant's chrome shape.
 *
 * Hygraph fragment fields are prefixed with `directory*` for content fields;
 * we strip the prefix on the chrome side because the surrounding type
 * (`DirectoryBaseChrome`) already namespaces them.
 */
function toCmsBodyData(pageData: DirectoryPageData): DirectoryCmsBodyData {
	const baseChrome: DirectoryBaseChrome = {
		eyebrow: pageData.directoryEyebrow,
		title: pageData.directoryTitle,
		descHtml: pageData.directoryDesc,
		searchPlaceholder: pageData.searchPlaceholder,
		industryFilterLabel: pageData.industryFilterLabel,
		noResultsLabel: pageData.noResultsLabel,
	};

	switch (pageData.variant) {
		case CosmosEnumDirectoryVariant.GoldInstructors:
			return { variant: pageData.variant, cards: pageData.cards, chrome: baseChrome };
		case CosmosEnumDirectoryVariant.TrainingPartners:
		case CosmosEnumDirectoryVariant.AcademicPartners:
			return { variant: pageData.variant, cards: pageData.cards, chrome: baseChrome };
		case CosmosEnumDirectoryVariant.ServicePartners: {
			// English fallback so the rendered combobox always has an accessible
			// name even when the CMS field is null. Localized override comes from
			// `pageData.serviceFilterLabel` when set.
			const serviceChrome: DirectoryServiceChrome = {
				...baseChrome,
				serviceFilterLabel: pageData.serviceFilterLabel ?? "",
				serviceFilterPlaceholder: pageData.serviceFilterPlaceholder ?? "Filter by services",
				badgeFilterLabel: pageData.badgeFilterLabel,
			};
			return {
				variant: pageData.variant,
				cards: pageData.cards,
				chrome: serviceChrome,
				badgeAssets: pageData.badgeAssets,
			};
		}
		default: {
			const exhaustiveCheck: never = pageData;
			throw new Error(
				`Unhandled DirectoryPageData variant: ${String((exhaustiveCheck as { variant?: string }).variant)}`,
			);
		}
	}
}

/**
 * Client-side renderer for `CosmosPageDirectory`.
 *
 * Composition order on the page:
 *   1. Hero
 *   2. topRefSections (in editor-defined order — includes the spotlight
 *      heading for GoldInstructors and the grid heading for all variants)
 *   3. Weekly Spotlight strip (GoldInstructors variant only — page-template
 *      injects under the last topRefSection, where the editor places the
 *      spotlight heading section)
 *   4. Directory grid body (filter chrome + cards + pagination)
 *   5. bottomRefSections
 */
export const CosmosPageDirectoryClient = ({ pageData, componentContext }: Props): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useAuth(!ctx.enableAuth);

	// Single page-h1 coordination across the directory's stacked regions. Priority for who
	// owns the <h1>: hero > first topRefSection > DirectoryHeading (directoryTitle) > first
	// bottomRefSection. getSections promotes its first hero-less section to h1, so each region
	// is told hasHero=true once anything earlier already owns the heading — preventing a second
	// h1 while still guaranteeing one (e.g. /training, which has only a bottom heading section).
	const hero = getHero(pageData.hero);
	// Only a hero that renders an h1 owns it; a decorative hero must not suppress the
	// directory's h1 promotion — gate on heroOwnsHeading, not mere presence.
	const heroPresent = heroOwnsHeading(pageData.hero);
	const topPresent = Boolean(pageData.topRefSections?.length);
	const directoryTitlePresent = Boolean(pageData.directoryTitle);

	return (
		<TemplateProviders componentContext={ctx}>
			{hero}
			{getSections(pageData.topRefSections, undefined, heroPresent)}
			{/*
				Weekly Spotlight injects between topRefSections and the main grid for
				the GoldInstructors variant only. The editor places the heading section
				at the end of topRefSections; the cards render directly below it.
			*/}
			{pageData.variant === CosmosEnumDirectoryVariant.GoldInstructors ? (
				<>
					<InstructorSpotlight instructors={pageData.spotlight} />
					{/*
						Divider visually separates the Weekly Spotlight strip from the main
						directory grid. `DividerSection` uses `disablePaddingBlock` so the
						spacing comes from neighboring sections — splitting evenly between
						the spotlight's padding-block-end and the grid's padding-block-start.
					*/}
					<DividerSection size={CosmosEnumSectionSizes.Sm} edsBackgroundColor={null} />
				</>
			) : null}
			{/* The directory title is the page h1 only when nothing above it already owns the top
			    heading — i.e. no hero AND no topRefSections (which, for GoldInstructors, carry the
			    Weekly Spotlight heading). Otherwise it stays h2 to avoid a second h1. */}
			<DirectoryCmsBody data={toCmsBodyData(pageData)} hasHero={heroPresent || topPresent} heroPresent={heroPresent} />
			{getSections(pageData.bottomRefSections, undefined, heroPresent || topPresent || directoryTitlePresent)}
		</TemplateProviders>
	);
};
